import { useEffect, useState } from 'react';
import { INSTALLERS, installerPath } from '../site';

const PENDING = {
  x64: {
    pendingLabel: 'x64 build coming soon',
    fine: 'The x64 installer is on the way.',
  },
  arm64: {
    pendingLabel: 'ARM64 build coming soon',
    fine: 'The ARM64 installer is on the way.',
  },
};

let pending;

function rendererHint() {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl');
    if (!gl) return '';
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    const renderer = ext ? String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) || '') : '';
    const lose = gl.getExtension('WEBGL_lose_context');
    if (lose) lose.loseContext();
    return renderer;
  } catch (err) {
    return '';
  }
}

async function detectWindowsArch() {
  const ua = navigator.userAgent || '';
  const platform = navigator.userAgentData?.platform || navigator.platform || '';
  const onWindows = /windows/i.test(platform) || /Windows/i.test(ua);
  if (!onWindows) return null;
  if (/arm64|aarch64/i.test(ua)) return 'arm64';

  let hinted = null;
  if (navigator.userAgentData?.getHighEntropyValues) {
    try {
      const hints = await navigator.userAgentData.getHighEntropyValues(['architecture', 'bitness']);
      const architecture = String(hints.architecture || '').toLowerCase();
      if (architecture === 'arm' || architecture === 'arm64' || architecture === 'aarch64') hinted = 'arm64';
      else if (architecture === 'x86' || architecture === 'x86_64' || architecture === 'amd64') hinted = 'x64';
    } catch (err) {
      /* Client hints can be blocked. Fall through. */
    }
  }

  // An x64 browser on Windows ARM still exposes the Qualcomm GPU. The native
  // installer is the one that should download.
  if (/adreno|qualcomm|snapdragon/i.test(rendererHint())) return 'arm64';
  return hinted;
}

async function published(arch) {
  const item = INSTALLERS[arch];
  const href = installerPath(item.name);
  try {
    const res = await fetch(href, { method: 'HEAD' });
    if (!res.ok) return null;
    const type = res.headers.get('content-type') || '';
    const disposition = res.headers.get('content-disposition') || '';
    const length = Number(res.headers.get('content-length') || 0);
    if (/text\/html/i.test(type) || /index\.html/i.test(disposition)) return null;
    const named = disposition.includes(item.name);
    const largeExe = length >= 1000000 && /octet-stream|x-msdownload|dosexec|executable/i.test(type);
    if (!named && !largeExe) return null;
    return { ...item, href };
  } catch (err) {
    return null;
  }
}

function choiceFor(arch, found) {
  const release = arch ? found[arch] : null;
  if (release) {
    return {
      ready: true,
      href: release.href,
      name: release.name,
      platform: release.platform,
      short: release.short,
      button: release.button,
      fine: release.fine,
      pendingLabel: release.button,
    };
  }

  if (arch && INSTALLERS[arch]) {
    const item = INSTALLERS[arch];
    const pendingCopy = PENDING[arch];
    return {
      ready: true,
      href: null,
      name: item.name,
      platform: item.platform,
      short: item.short,
      button: item.button,
      fine: pendingCopy.fine,
      pendingLabel: pendingCopy.pendingLabel,
    };
  }

  const arm = found.arm64;
  if (arm) {
    return {
      ready: true,
      href: arm.href,
      name: arm.name,
      platform: arm.platform,
      short: arm.short,
      button: arm.button,
      fine: 'Windows 10 or later, ARM64. The x64 build is on the way.',
      pendingLabel: arm.button,
    };
  }

  return {
    ready: true,
    href: null,
    name: '',
    platform: 'Windows 10 or later',
    short: 'Windows',
    button: 'Download for Windows',
    fine: 'Windows 10 or later. Installs for your account.',
    pendingLabel: 'Download coming soon',
  };
}

function loadInstaller() {
  if (!pending) {
    pending = (async () => {
      const [arch, arm64, x64] = await Promise.all([
        detectWindowsArch(),
        published('arm64'),
        published('x64'),
      ]);
      return choiceFor(arch, { arm64, x64 });
    })();
  }
  return pending;
}

const WAITING = {
  ready: false,
  href: null,
  name: '',
  platform: 'Windows 10 or later',
  short: 'Windows',
  button: 'Download for Windows',
  fine: 'Windows 10 or later. Installs for your account.',
  pendingLabel: 'Download for Windows',
};

export default function useInstaller() {
  const [installer, setInstaller] = useState(WAITING);

  useEffect(() => {
    let live = true;
    loadInstaller().then((next) => {
      if (live) setInstaller(next);
    });
    return () => {
      live = false;
    };
  }, []);

  return installer;
}
